import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import API from '../../../services/API'

/**
 * Blocking prompt shown on a PTS organisation page when the organisation has
 * no base location. The base drives base-to-base mileage billing and is
 * stamped onto hospital-portal bookings, so it must be set before the
 * organisation is used.
 */
const BaseLocationRequiredDialog = ({ client, onSaved }) => {
  const navigate = useNavigate()
  const [baseOptions, setBaseOptions] = useState([])
  const [selectedBase, setSelectedBase] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    API.get('/base-location-options')
      .then((res) => {
        if (res.status === 200) {
          const options = res.data || []
          setBaseOptions(options)
          if (options.length === 1) setSelectedBase(options[0])
        }
      })
      .catch((e) => console.error(e))
  }, [])

  const save = async () => {
    if (!selectedBase?._id) return
    setSaving(true)
    setError(null)
    try {
      const res = await API.put(
        `/pts/organisations/${client._id}/base-location`,
        { baseLocation: selectedBase._id },
        { skipGlobalErrorHandler: true },
      )
      if (res.status === 200) {
        onSaved?.()
      } else if (res.status === 403) {
        setError(
          'You need PTS co-ordinator access to set the base location. Please ask a co-ordinator to complete this.',
        )
      } else {
        setError('Unable to save the base location. Please try again.')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to save the base location. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon color="warning" />
          <span>Base location required</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={0.5}>
          <Typography color="text.secondary">
            {client?.orgName || 'This organisation'} has no base location.
            Bookings for this organisation — including hospital portal
            requests — are billed base-to-base, so a base location must be set
            before continuing.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            options={baseOptions}
            value={selectedBase}
            onChange={(event, base) => setSelectedBase(base)}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) =>
              String(option?._id) === String(value?._id)
            }
            renderInput={(params) => (
              <TextField {...params} label="Base location" required />
            )}
          />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            gap={1}
          >
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Go back
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={!selectedBase?._id || saving}
            >
              {saving ? 'Saving…' : 'Save base location'}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default BaseLocationRequiredDialog
